import React from "react";
import PropTypes from "prop-types";

import { Drawer } from "@lululemon/mwa-selective-comparison";
import { useLaunchDarklyFlag } from "@lululemon/mwa-launch-darkly";
import { trackEvent, EVENT_CATEGORY } from "@lululemon/mwa-analytics";
import { publicConfig } from "@lululemon/ecom-common/src/util/public-config";
import DynamicLinkProvider from "@lululemon/ecom-common/src/components/dynamic-link-provider";

const SelectiveComparisonDrawer = ({
  cdpUrl,
  cdpLabel,
  isMobile,
  isAboveFooterWaypoint,
  productsToCompare,
  shouldDisplaySelectiveComparison,
  clearAllProductsToCompare,
  removeProductToCompareById,
  unFocusComparisonDrawer,
  comparisonDrawerRef,
  maxProductsToCompare,
  allowScrollMobile,
}) => {
  const isAcclCompareLinksTriggerFullPageTurnLDFlag = useLaunchDarklyFlag(
    "accl-compare-links-trigger-full-page-turn",
    false,
    publicConfig?.launchDarkly?.clientSideId
  );

  const getComparePageURL = () => {
    const products = productsToCompare
      .map((p) => p?.productUrl || p?.pdpUrl)
      .join("|");

    const qs = {
      products,
      cdpUrl,
      cdpLabel,
    };

    const params = new URLSearchParams(qs);

    const compareUrl = `/c/compare?${params.toString()}`;
    // ACCL todo - this was added to do a server-side redirect
    if (isAcclCompareLinksTriggerFullPageTurnLDFlag) {
      const { origin, pathname } =
        typeof window !== "undefined" ? window.location : {};

      const locale = pathname?.match(/^\/([a-z]{2}-[a-z]{2})\//i)?.[1] || "";

      return origin + (locale ? `/${locale}` : "") + compareUrl;
    }

    return compareUrl;
  };

  const comparePageUrl = getComparePageURL();

  if (
    isAcclCompareLinksTriggerFullPageTurnLDFlag &&
    shouldDisplaySelectiveComparison
  ) {
    return (
      <Drawer
        clearAllProductsToCompare={clearAllProductsToCompare}
        comparePageUrl={comparePageUrl}
        EVENT_CATEGORY={EVENT_CATEGORY}
        isAboveFooterWaypoint={isAboveFooterWaypoint}
        isMobile={isMobile}
        maxProductsToCompare={maxProductsToCompare}
        productsToCompare={productsToCompare}
        removeProductById={removeProductToCompareById}
        trackEvent={trackEvent}
        allowScrollMobile={allowScrollMobile}
        unFocusComparisonDrawer={unFocusComparisonDrawer}
        headerButtonRef={comparisonDrawerRef}
      />
    );
  }

  return (
    shouldDisplaySelectiveComparison && (
      <DynamicLinkProvider>
        <Drawer
          clearAllProductsToCompare={clearAllProductsToCompare}
          comparePageUrl={comparePageUrl}
          EVENT_CATEGORY={EVENT_CATEGORY}
          isAboveFooterWaypoint={isAboveFooterWaypoint}
          isMobile={isMobile}
          maxProductsToCompare={maxProductsToCompare}
          productsToCompare={productsToCompare}
          removeProductById={removeProductToCompareById}
          trackEvent={trackEvent}
          allowScrollMobile={allowScrollMobile}
          unFocusComparisonDrawer={unFocusComparisonDrawer}
          headerButtonRef={comparisonDrawerRef}
        />
      </DynamicLinkProvider>
    )
  );
};

SelectiveComparisonDrawer.propTypes = {
  cdpUrl: PropTypes.string.isRequired,
  cdpLabel: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isAboveFooterWaypoint: PropTypes.bool.isRequired,
  productsToCompare: PropTypes.arrayOf(
    PropTypes.shape({
      productUrl: PropTypes.string,
      pdpUrl: PropTypes.string,
    })
  ).isRequired,
  shouldDisplaySelectiveComparison: PropTypes.bool,
  clearAllProductsToCompare: PropTypes.func.isRequired,
  removeProductToCompareById: PropTypes.func.isRequired,
  unFocusComparisonDrawer: PropTypes.func.isRequired,
  comparisonDrawerRef: PropTypes.object,
  maxProductsToCompare: PropTypes.number,
  allowScrollMobile: PropTypes.bool,
};

SelectiveComparisonDrawer.defaultProps = {
  comparisonDrawerRef: null,
  shouldDisplaySelectiveComparison: false,
  maxProductsToCompare: 3,
  allowScrollMobile: false,
};

export default SelectiveComparisonDrawer;
